import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { trackUsageEvent } from '../usage-events';

interface QueueItem {
  id: string;
  type: string | null;
  status: string;
  priority: string;
  description: string;
  due_at: string | null;
  due_date: string | null;
  waiting_on: string | null;
  blocked_reason: string | null;
  contact_id: string | null;
  source: string | null;
  source_ref: string | null;
  created_at: string | null;
  updated_at: string | null;
  is_overdue: boolean;
  is_due_today: boolean;
}

interface QueueResponse {
  generated_at: string;
  freshness: { newest_item_at: string | null; oldest_active_at: string | null };
  counts: { overdue: number; due_today: number; blocked: number; waiting: number };
  items: QueueItem[];
}

interface EngineHealth {
  is_stale: boolean;
  last_successful_ingest_at: string | null;
  minutes_since_last_success: number | null;
  stale_threshold_minutes: number;
  // Returned by /api/admin/engine-health; consumed here for the honest call-volume readout.
  recent_call_volume?: { last_24h: number; last_7d: number } | null;
  recent_contact_link_rate?: number | null;
}

type Filter = 'all' | 'overdue' | 'today';

type State =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; queue: QueueResponse; engine: EngineHealth | null };

function formatAge(iso: string | null): string {
  if (!iso) return 'No data';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return 'No data';
  const minutes = Math.max(0, Math.round((Date.now() - then.getTime()) / 60_000));
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function typeLabel(type: string | null): string {
  if (!type) return 'Unclassified';
  return type.replaceAll('_', ' ');
}

function dueLabel(item: QueueItem): string {
  if (item.is_overdue) return 'Overdue';
  if (item.is_due_today) return 'Due today';
  if (item.due_date) return `Due ${item.due_date}`;
  if (item.due_at) return `Due ${new Date(item.due_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  return 'No due date';
}

function missingQualification(item: QueueItem): string[] {
  const chips: string[] = [];
  const text = `${item.description} ${item.source ?? ''}`.toLowerCase();
  if (!item.contact_id) chips.push('contact');
  if (!text.includes('budget') && !text.includes('$')) chips.push('budget');
  if (!text.includes('timeline') && !text.includes('date')) chips.push('timeline');
  if (!text.includes('van') && !text.includes('sprinter') && !text.includes('revel')) chips.push('vehicle');
  return chips.slice(0, 4);
}

function itemMatches(item: QueueItem, types: string[], statuses: string[] = []): boolean {
  return (item.type !== null && types.includes(item.type)) || statuses.includes(item.status);
}

export function RonHome(): JSX.Element {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [filter, setFilter] = useState<Filter>('all');

  useEffect(() => {
    void trackUsageEvent('ron_card_opened', { route: '/ron' });
    let cancelled = false;
    (async () => {
      try {
        const [queueRes, engineRes] = await Promise.all([
          fetch('/api/queues/ron-follow-up', { credentials: 'include' }),
          fetch('/api/admin/engine-health', { credentials: 'include' }),
        ]);
        if (cancelled) return;
        if (!queueRes.ok) {
          const body = (await queueRes.json().catch(() => ({}))) as { error?: string };
          setState({ kind: 'error', message: body.error ?? `Queue request failed: ${queueRes.status}` });
          return;
        }
        const queue = (await queueRes.json()) as QueueResponse;
        const engine = engineRes.ok ? ((await engineRes.json()) as EngineHealth) : null;
        setState({ kind: 'ready', queue, engine });
      } catch (err) {
        if (!cancelled) setState({ kind: 'error', message: (err as Error).message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const sections = useMemo(() => {
    if (state.kind !== 'ready') return null;
    const items = state.queue.items;
    return {
      needsToday: items.filter((i) => i.is_overdue || i.is_due_today || i.priority === 'urgent' || i.priority === 'high').slice(0, 5),
      newLeads: items.filter((i) => itemMatches(i, ['intake_missing_info', 'quote_review_scheduling', 'reactivation'])),
      waiting: items.filter((i) => itemMatches(i, ['customer_waiting_followup'], ['waiting'])),
      readyForErik: items.filter((i) => i.waiting_on?.toLowerCase().includes('erik') || i.status === 'blocked'),
      followups: items.filter((i) => itemMatches(i, ['customer_success_followup'])),
      recent: [...items]
        .sort((a, b) => new Date(b.updated_at ?? b.created_at ?? 0).getTime() - new Date(a.updated_at ?? a.created_at ?? 0).getTime())
        .slice(0, 5),
    };
  }, [state]);

  if (state.kind === 'loading') {
    return <div className="rounded-lg bg-white ring-1 ring-slate-200 p-6 text-sm text-slate-500">Loading Ron card...</div>;
  }

  if (state.kind === 'error') {
    return <div className="rounded-lg bg-rose-50 ring-1 ring-rose-200 p-4 text-sm text-rose-700">Error: {state.message}</div>;
  }

  const openCount = state.queue.items.length;

  // CS-03: tiles drill into the queue. 'overdue'/'today' show a filtered flat list; 'all' shows the buckets.
  const filtered =
    filter === 'overdue'
      ? state.queue.items.filter((i) => i.is_overdue)
      : filter === 'today'
        ? state.queue.items.filter((i) => i.is_due_today)
        : [];

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Ron daily status</h2>
          <p className="mt-1 text-sm text-slate-500">Customer follow-ups and handoffs that need movement.</p>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <MetricTile label="Open" value={openCount} active={filter === 'all'} onSelect={() => setFilter('all')} />
          <MetricTile
            label="Overdue"
            value={state.queue.counts.overdue}
            tone={state.queue.counts.overdue > 0 ? 'danger' : 'neutral'}
            active={filter === 'overdue'}
            onSelect={() => setFilter((f) => (f === 'overdue' ? 'all' : 'overdue'))}
          />
          <MetricTile
            label="Due today"
            value={state.queue.counts.due_today}
            tone={state.queue.counts.due_today > 0 ? 'warning' : 'neutral'}
            active={filter === 'today'}
            onSelect={() => setFilter((f) => (f === 'today' ? 'all' : 'today'))}
          />
        </div>
      </header>

      <TrustStrip engine={state.engine} />

      {openCount === 0 ? (
        // CS-05: human empty state; the machinery moves behind a "Why?" disclosure.
        <div className="rounded-lg bg-white ring-1 ring-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-900">You&rsquo;re all caught up</h3>
          <p className="mt-1 text-sm text-slate-500">No open follow-ups need movement right now.</p>
          <details className="mt-3 text-xs text-slate-500">
            <summary className="cursor-pointer text-slate-400 hover:text-slate-700">Why is this empty?</summary>
            <p className="mt-2 rounded bg-slate-50 px-3 py-2 font-mono text-[11px] leading-relaxed text-slate-500">
              queue endpoint returned 0 active items · no AI summary generated · a stale evidence source can hide
              follow-ups — see source coverage above.
            </p>
          </details>
        </div>
      ) : filter !== 'all' ? (
        <FilteredList filter={filter} items={filtered} onClear={() => setFilter('all')} />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <Section title="Needs Ron today" items={sections?.needsToday ?? []} showChips />
          <Section title="New / unqualified leads" items={sections?.newLeads ?? []} qualificationChips />
          <Section title="Waiting on customer" items={sections?.waiting ?? []} />
          <Section title="Ready for Erik" items={sections?.readyForErik ?? []} />
          <Section title="CS / closed-out follow-ups" items={sections?.followups ?? []} />
          <Section title="Recent changes" items={sections?.recent ?? []} />
        </div>
      )}
    </div>
  );
}

// CS-01 (honest): overall heartbeat is real; call volume is real. Voicemail / SMS / web forms have NO
// per-source liveness in this endpoint yet, so we surface that gap explicitly instead of implying "all green."
// True per-source health is tracked in INF-1461; until it lands, a stale voicemail feed would NOT turn this red.
function TrustStrip({ engine }: { engine: EngineHealth | null }): JSX.Element {
  // Three states, never a default-green: 'unknown' when the health check itself is unreachable
  // (engine === null) — otherwise a downed health endpoint would silently read green, which is the
  // exact CS-01 failure mode the audit flagged.
  const status: 'unknown' | 'stale' | 'current' = engine == null ? 'unknown' : engine.is_stale ? 'stale' : 'current';
  const vol = engine?.recent_call_volume ?? null;
  const linkRate = engine?.recent_contact_link_rate ?? null;
  const tone =
    status === 'stale'
      ? { box: 'bg-rose-50 ring-rose-200', text: 'text-rose-800', dot: 'bg-rose-500' }
      : status === 'unknown'
        ? { box: 'bg-amber-50 ring-amber-200', text: 'text-amber-800', dot: 'bg-amber-500' }
        : { box: 'bg-emerald-50 ring-emerald-200', text: 'text-emerald-800', dot: 'bg-emerald-500' };
  return (
    <section className={`rounded-lg ring-1 text-sm ${tone.box}`} aria-label="Evidence source health">
      <div className={`flex flex-wrap items-center gap-x-2 gap-y-1 px-4 py-3 ${tone.text}`}>
        <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${tone.dot}`} />
        {status === 'unknown' ? (
          <span className="font-medium">Sales Engine health unavailable &mdash; source status unknown.</span>
        ) : (
          <>
            <span className="font-medium">Sales Engine {status === 'stale' ? 'stale' : 'current'}.</span>
            <span>Last ingest {formatAge(engine?.last_successful_ingest_at ?? null)}.</span>
            {vol && <span className="text-slate-600">&middot; Calls 24h/7d {vol.last_24h}/{vol.last_7d}</span>}
            {typeof linkRate === 'number' && (
              <span className="text-slate-600">&middot; link rate {Math.round(linkRate * 100)}%</span>
            )}
          </>
        )}
      </div>
      <div className="border-t border-slate-200/70 px-4 py-2.5 text-xs text-slate-600">
        <span className="font-medium text-slate-700">Monitored:</span> overall ingest, calls.{' '}
        <span className="font-medium text-slate-700">Not yet monitored:</span> voicemail, SMS, web forms — a stale feed on
        those would not show here.{' '}
        <a
          href="https://linear.app/overlanddesignbuild/issue/INF-1461"
          target="_blank"
          rel="noreferrer"
          className="underline hover:text-slate-900"
        >
          Per-source health: INF-1461
        </a>
      </div>
    </section>
  );
}

function FilteredList({ filter, items, onClear }: { filter: Exclude<Filter, 'all'>; items: QueueItem[]; onClear: () => void }): JSX.Element {
  const label = filter === 'overdue' ? 'Overdue' : 'Due today';
  return (
    <section className="rounded-lg bg-white ring-1 ring-slate-200">
      <header className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5">
        <h3 className="text-sm font-semibold text-slate-900">
          {label} <span className="text-slate-400">· {items.length}</span>
        </h3>
        <button type="button" onClick={onClear} className="text-xs text-slate-500 underline hover:text-slate-900">
          Clear filter
        </button>
      </header>
      {items.length === 0 ? (
        <div className="px-4 py-5 text-sm text-slate-500">Nothing {label.toLowerCase()}.</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((item) => (
            <QueueRow key={item.id} item={item} showChips />
          ))}
        </ul>
      )}
    </section>
  );
}

function MetricTile({
  label,
  value,
  tone = 'neutral',
  active,
  onSelect,
}: {
  label: string;
  value: number;
  tone?: 'neutral' | 'warning' | 'danger';
  active: boolean;
  onSelect: () => void;
}): JSX.Element {
  const toneClass = tone === 'danger' ? 'text-rose-700' : tone === 'warning' ? 'text-amber-700' : 'text-slate-900';
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      className={`min-w-20 rounded-lg bg-white px-3 py-2 text-center transition ${
        active ? 'ring-2 ring-slate-900' : 'ring-1 ring-slate-200 hover:ring-slate-400'
      }`}
      title={label === 'Open' ? 'Show all buckets' : `Filter to ${label.toLowerCase()}`}
    >
      <div className={`text-lg font-semibold ${toneClass}`}>{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </button>
  );
}

function QueueRow({ item, showChips = false, qualificationChips = false }: { item: QueueItem; showChips?: boolean; qualificationChips?: boolean }): JSX.Element {
  return (
    <li className="px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <p className="min-w-0 text-sm text-slate-900">{item.description}</p>
        {item.contact_id && (
          <Link to={`/contacts/${item.contact_id}`} className="shrink-0 text-xs text-slate-500 underline hover:text-slate-900">
            Contact
          </Link>
        )}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">{typeLabel(item.type)}</span>
        <span>{dueLabel(item)}</span>
        <span>{item.priority}</span>
        {showChips && item.waiting_on && <span>waiting on {item.waiting_on}</span>}
        {qualificationChips &&
          missingQualification(item).map((chip) => (
            <span key={chip} className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-700 ring-1 ring-amber-200">
              missing {chip}
            </span>
          ))}
      </div>
    </li>
  );
}

function Section({
  title,
  items,
  showChips = false,
  qualificationChips = false,
}: {
  title: string;
  items: QueueItem[];
  showChips?: boolean;
  qualificationChips?: boolean;
}): JSX.Element {
  return (
    <section className="rounded-lg bg-white ring-1 ring-slate-200">
      <header className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        <span className="text-xs text-slate-500">{items.length}</span>
      </header>
      {items.length === 0 ? (
        <div className="px-4 py-5 text-sm text-slate-500">Nothing in this bucket.</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((item) => (
            <QueueRow key={item.id} item={item} showChips={showChips} qualificationChips={qualificationChips} />
          ))}
        </ul>
      )}
    </section>
  );
}
